import type { DeviceState } from "../types/DeviceStates";

/**
 * Device state helper utilities
 *
 * Pure utility functions for working with device states.
 * These help Layer 2 & 3 without adding business logic to components.
 */

/**
 * Stat tone type (matches StatList component)
 */
export type StatTone = "success" | "warning" | "error" | "info" | "neutral";

/**
 * Map device state to StatList tone for conditional styling
 *
 * @param state - Device state
 * @returns Appropriate tone for StatList component
 *
 * @example
 * ```tsx
 * const tone = getStateTone("charging"); // "success"
 * <StatList items={[...]} tone={tone} />
 * ```
 */
export function getStateTone(state: DeviceState): StatTone {
  switch (state) {
    case "charging":
    case "on":
      return "success";
    case "discharging":
      return "warning";
    case "heat":
    case "cool":
      return "info";
    case "off":
    case "idle":
    case "eco":
    case "auto":
      return "neutral";
    case "unknown":
    default:
      return "neutral";
  }
}

// Re-export utility functions from DeviceStates to avoid duplication
export {
  getDeviceStateLabel as getDeviceStateDisplayLabel,
  isActiveState as isActiveDeviceState,
} from "../types/DeviceStates";
